import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CustomText from "../atomic/CustomText";
import PatientDropDownItemProps from "../../services/models/patientDropdownItemProps.model";
import Button from "../organism/Button";
import SearchInputProps from "../../services/models/patientSearchInputProps.model";
import { PopUpContext } from "../../services/context/popupContext";
import CreatePatientPopup from "../popups/createPatientPopup";
import useSearchPatients from "../../services/utils/custom hooks/Patients/useSearchPatients";
import useGlobalSearch from "../../services/utils/custom hooks/useGlobalSearch";
import LoadingSvg from "../../assets/svg/LoadingSVG";
import { useHistory, useLocation } from "react-router-dom";
import PatientInfoModel from "../../services/models/formModels/patientInfo.model";
import OnlineOrderPopupValues from "../../services/models/formModels/onlineOrderPopupValues.model";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import CustomCalendar from "./CustomCalendar/CustomCalendar";
import { useAdminContext } from "../../services/context/TourPopupContext";
import dataConstants from "../../constants/dataConstants";
import CalendarIcon from "../../assets/svg/Calendar";
import SearchIcon from "../../assets/svg/Search";
import SearchSection from "./GlobalSearch/SearchSection";
import AddPatientButton from "./GlobalSearch/AddPatientButton";
import NavigationSection from "./GlobalSearch/NavigationSection";
import { matchNavigationLinks } from "./GlobalSearch/navigationMatcher";
import navigationLinks from "./GlobalSearch/navigationLinks";
import useFeatureFlags from "../../services/utils/custom hooks/Feature Flagging/useFeatureFlags";
import { useDebouncedDateDetection } from "../../services/utils/custom hooks/useDebouncedDateDetection";

export type SearchMode = "dropdown" | "datalist";
interface PatientSearchProps {
  readonly mode: SearchMode;
  readonly containerClassName?: string;
  readonly onSearch?: (searchQuery: string) => void;
  readonly oktaAuth?: any;
  readonly closeInput: () => void;
}

function formatDOB(dateString: string): string {
  if (!dateString) return "---";
  // Handle both formats: "2025-11-21" and "11-03-2009"
  if (dateString.includes("-")) {
    const parts = dateString.split("-");
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        // Format: YYYY-MM-DD
        return `${parts[1]}-${parts[2]}-${parts[0].slice(2)}`;
      } else {
        // Format: MM-DD-YYYY
        return dateString;
      }
    }
  }
  return dateString;
}

function PatientDropDownItem({
  firstName,
  lastName,
  ssn,
  mrn,
  id,
  mode,
  birthDate,
  clearInputText,
  ...rest
}: Readonly<PatientDropDownItemProps>): JSX.Element {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { setVisible } = useContext(PopUpContext);
  const orgs = queryClient.getQueryData<any>(["organizations"]);
  const fullName = `${firstName} ${lastName}`;
  const formattedBirthDate = birthDate.split("-");
  // delete rest.organizationId;

  const patientInformation: PatientInfoModel = {
    id,
    firstName,
    lastName,
    birthDate: `${formattedBirthDate[1]}-${formattedBirthDate[2]}-${formattedBirthDate[0]}`,
    ...rest,
    securityInfo: {
      ssn,
      mrn,
    },
  };

  function handlePatientClick(e: any) {
    e.stopPropagation();

    if (mode === "dropdown") {
      history.push(`/access/patients/${patientInformation.id}`);
      clearInputText();
    } else {
      history.push(`/access/online-order`, {
        patientInformation: JSON.stringify(patientInformation),
      });
      setVisible(false);
    }
    rest?.setShowList?.(false);
    rest?.closeInput?.();
  }

  return (
    <li
      onClick={handlePatientClick}
      className="w-full p-0 m-0 !text-royalBlue list-none cursor-pointer hover:bg-[rgba(0,0,0,0.05)]"
    >
      <div
        className={`md:flex justify-start items-center px-4 py-1.5 ${
          mode === "datalist"
            ? "bg-white border border-dropDownBorder mb-1 rounded-md"
            : "border-t border-roundedInputBorder"
        }`}
      >
        <div
          // data-openreplay-obscured
          data-dd-privacy="mask"
          className={`flex flex-wrap md:flex-nowrap my-2 md:my-0 justify-start ${
            mode === "datalist" ? "w-full md:w-4/6" : "w-[40%]"
          } overflow-y-scroll truncate  md:text-base text-xs`}
        >
          <CustomText
            text={fullName}
            fontWeight="font-medium"
            fontSize="text-sm"
            className={`${
              mode === "datalist"
                ? "before:content-profileIcon before:mx-2 before:align-middle"
                : "text-left"
            }`}
          />
        </div>
        <div
          // data-openreplay-obscured
          data-dd-privacy="mask"
          className={`flex flex-wrap md:flex-nowrap my-2 md:my-0 justify-start ${
            mode === "datalist" ? "w-full md:w-4/6" : "w-[70%]"
          } overflow-y-scroll truncate  md:text-base text-xs`}
        >
          <span className="mx-2">
            <CustomText
              text={`DOB:`}
              fontWeight="font-bold"
              className="text-left"
            />
          </span>
          <CustomText
            text={birthDate ? moment(birthDate).format("MM-DD-YY") : "---"}
            fontWeight="font-normal"
            className="text-left"
          />
          <span className="mx-2">
            <CustomText
              text={`SSN:`}
              fontWeight="font-bold"
              className="text-left"
            />
          </span>
          <CustomText
            text={!!ssn ? ssn : "---"}
            fontWeight="font-normal"
            className="text-left"
          />
          <span className="mx-2">
            <CustomText
              text={`MRN:`}
              fontWeight="font-bold"
              className="text-left"
            />
          </span>
          <CustomText
            text={!!mrn ? mrn : "---"}
            fontWeight="font-normal"
            className="text-left"
          />
        </div>
        {mode === "datalist" && (
          <Button
            buttonTheme="transparent"
            onClick={handlePatientClick}
            textInformation={{ text: "select", fontWeight: "font-semibold" }}
            className="text-primary ml-2 "
          />
        )}
      </div>
      {mode === "dropdown" && (
        <p className="w-full flex items-center px-2 py-1.5 ">
          <span className="mx-2">
            <CustomText
              text={`ORG:`}
              fontWeight="font-bold"
              className="text-left"
            />
          </span>
          <CustomText
            text={
              !!rest.organizationId
                ? orgs?.data?.find(
                    (org: any) => org.fhirOrganizationId === rest.organizationId
                  ).name
                : "---"
            }
            fontWeight="font-normal"
            className="text-left"
          />
        </p>
      )}
    </li>
  );
}

function PatientsList({
  data,
  mode,
  searching,
  setShowList,
  clearInputText,
  searchText,
  closeInput,
}: {
  readonly data: PatientDropDownItemProps[];
  readonly mode: SearchMode;
  readonly searching: boolean;
  readonly setShowList: (val: boolean) => void;
  readonly clearInputText?: () => void;
  readonly searchText: string | null;
  readonly closeInput: () => void;
}): JSX.Element {
  if (searching) {
    return (
      <div className="py-3 text-center flex justify-center">
        <LoadingSvg isSecondary className="animate-spin" />
      </div>
    );
  }
  if (data.length === 0 && searchText)
    return (
      <div className="py-3 text-center">
        <CustomText text="No record found" fontWeight="font-semibold" />
      </div>
    );

  return (
    <div className={`${searchText ? "py-3" : ""}`}>
      {data.map((patient) => (
        <PatientDropDownItem
          setShowList={setShowList}
          clearInputText={clearInputText}
          key={patient.birthDate}
          {...patient}
          closeInput={closeInput}
          mode={mode}
        />
      ))}
    </div>
  );
}

// Helper function to highlight matching query text in patient names
function highlightMatchingText(text: string, query: string): JSX.Element {
  if (!query || !text) {
    return <>{text}</>;
  }

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const parts: Array<{ text: string; isMatch: boolean }> = [];
  let lastIndex = 0;
  let searchIndex = 0;

  while (searchIndex < lowerText.length) {
    const matchIndex = lowerText.indexOf(lowerQuery, searchIndex);

    if (matchIndex === -1) {
      // No more matches, add remaining text
      if (lastIndex < text.length) {
        parts.push({ text: text.substring(lastIndex), isMatch: false });
      }
      break;
    }

    // Add text before match
    if (matchIndex > lastIndex) {
      parts.push({
        text: text.substring(lastIndex, matchIndex),
        isMatch: false,
      });
    }

    // Add matched text
    parts.push({
      text: text.substring(matchIndex, matchIndex + query.length),
      isMatch: true,
    });

    lastIndex = matchIndex + query.length;
    searchIndex = matchIndex + 1;
  }

  return (
    <>
      {parts.map((part, index) =>
        part.isMatch ? (
          <span
            key={`match-${index}-${part.text.substring(0, 3)}`}
            className="font-bold"
          >
            {part.text}
          </span>
        ) : (
          <span key={`text-${index}-${part.text.substring(0, 3)}`}>
            {part.text}
          </span>
        )
      )}
    </>
  );
}

function GlobalSearchDropdown({
  searchText,
  dropDownRef,
  oktaAuth,
  setShowList,
  clearInputText,
  showDateInput,
  setShowDateInput,
  setPatientDOB,
  closeInput,
  searchQuery,
  patientDob,
  resetSearch,
}: {
  readonly searchText: string | null;
  readonly dropDownRef: any;
  readonly oktaAuth: any;
  readonly setShowList: (val: boolean) => void;
  readonly clearInputText: () => void;
  readonly showDateInput: boolean;
  readonly setShowDateInput: any;
  readonly setPatientDOB: (date: any) => void;
  readonly closeInput: () => void;
  readonly searchQuery: string;
  readonly patientDob: string;
  readonly resetSearch: () => void;
}): JSX.Element {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { isSectionActive } = useFeatureFlags();
  const { visible, setVisible, setPopUp, setPopUpTitle } =
    useContext(PopUpContext);
  const { data, isLoading } = useGlobalSearch(
    searchQuery,
    patientDob,
    oktaAuth,
    5
  );

  const isLoadingPatients = isLoading;
  const isLoadingOrders = isLoading;

  // Access patients and orders data from the response
  // The API returns: { patients: { data: [], total: number }, orders: { data: [], total: number } }
  const patientsData = data?.patients?.data ?? [];
  const ordersData = data?.orders?.data ?? [];
  const patientsTotal = data?.patients?.total ?? 0;
  const ordersTotal = data?.orders?.total ?? 0;
  const orgs = queryClient.getQueryData<any>(["organizations"]);

  const availableNavigationLinks = useMemo(
    () =>
      navigationLinks.filter(
        (link) => !link.featureFlagKey || isSectionActive(link.featureFlagKey)
      ),
    [isSectionActive]
  );

  const navigationResults = useMemo(() => {
    if (!searchQuery?.trim()) return [];
    return matchNavigationLinks(searchQuery, {
      links: availableNavigationLinks,
    });
  }, [searchQuery, availableNavigationLinks]);

  const shouldShowNavigationSection =
    navigationResults.length > 0 && !!searchQuery?.trim();

  // Get the query string to display in empty state
  const displayQuery =
    searchQuery ||
    (patientDob ? moment(patientDob).format("MM-DD-YYYY") : searchText) ||
    "";

  /**
   * Parses the search text and returns an object with firstName, middleInitial, and lastName
   * Logic:
   * - 3+ spaces (4+ parts): firstName, middleInitial, lastName (first 3 parts)
   * - 2 spaces (3 parts): firstName, middleInitial, lastName
   * - 1 space (2 parts): firstName, lastName
   * - 0 spaces (1 part): firstName only
   */
  function parseSearchTextToPatientInfo(searchText: string | null): OnlineOrderPopupValues {
    const trimmedText = (searchText || "").trim();
    
    // Start with default values
    const defaultValues: OnlineOrderPopupValues = {
      firstName: "",
      middleInitial: "",
      lastName: "",
      birthDate: "",
      gender: "",
      email: "",
      address: "",
      city: "",
      state: "",
      phone: "",
      zipcode: "",
      securityInfo: {
        ssn: "",
        mrn: "",
      },
    };

    if (!trimmedText) {
      return defaultValues;
    }

    const parts = trimmedText.split(/\s+/).filter(part => part.length > 0);
    
    if (parts.length >= 3) {
      // 3+ parts: firstName, middleInitial (first letter), lastName
      return {
        ...defaultValues,
        firstName: parts[0],
        middleInitial: parts[1].charAt(0).toUpperCase(),
        lastName: parts.slice(2).join(" "), // Join remaining parts as lastName
      };
    } else if (parts.length === 2) {
      // 2 parts: firstName, lastName
      return {
        ...defaultValues,
        firstName: parts[0],
        middleInitial: "",
        lastName: parts[1],
      };
    } else {
      // 1 part: firstName only
      return {
        ...defaultValues,
        firstName: parts[0],
        middleInitial: "",
        lastName: "",
      };
    }
  }

  function handleAddPatient() {
    closeInput();
    
    // Parse the search text to extract patient name information
    const parsedPatientInfo = parseSearchTextToPatientInfo(searchText);
    
    setVisible(!visible);
    setPopUpTitle("Choose or Create a Patient");
    setPopUp(
      <CreatePatientPopup 
        oktaAuth={oktaAuth} 
        patientInformation={parsedPatientInfo}
      />
    );
    setShowList(false);
  }

  function handleViewMoreTestOrders() {
    closeInput();
    const params = new URLSearchParams();
    if (searchQuery) {
      params.append("query", searchQuery);
    }
    if (patientDob) {
      params.append("patientDOB", patientDob);
    }
    const queryParam = params.toString() ? `?${params.toString()}` : "";
    history.push(`/access/test-orders${queryParam}`);
    resetSearch();
    setShowList(false);
  }

  function handleViewMorePatients() {
    closeInput();
    const params = new URLSearchParams();
    if (searchQuery) {
      params.append("query", searchQuery);
    }
    if (patientDob) {
      params.append("patientDOB", patientDob);
    }
    const queryParam = params.toString() ? `?${params.toString()}` : "";
    history.push(`/access/patients${queryParam}`);
    resetSearch();
    setShowList(false);
  }

  function handleOrderClick(order: any) {
    closeInput();
    resetSearch();
    history.push(`/access/test-orders/summary/${order.id}`);
    setShowList(false);
  }

  function handlePatientClick(patientId: string) {
    closeInput();
    resetSearch();
    history.push(`/access/patients/${patientId}`);
    setShowList(false);
  }

  function handleNavigationClick(route: string) {
    closeInput();
    resetSearch();
    history.push(route);
    setShowList(false);
  }

  function getOrganizationName(organizationId: string): string {
    if (!organizationId || !orgs?.data) return "---";
    const org = orgs.data.find(
      (org: any) => org.fhirOrganizationId === organizationId
    );
    return org?.name || "---";
  }

  // Column configurations for Test Orders table
  const testOrdersColumns = [
    {
      key: "testType",
      label: "Test Type",
      accessor: (order: any) => {
        const testTypeName = order?.testTypes?.name;
        if (!testTypeName) return "---";
        const parts = testTypeName.split(":");
        return parts[0] || "---";
      },
    },
    {
      key: "patientName",
      label: "Patient Name",
      accessor: (order: any) =>
        `${order?.patientFirstName || ""} ${
          order?.patientLastName || ""
        }`.trim() || "---",
      highlight: true,
    },
    {
      key: "dob",
      label: "DOB",
      accessor: (order: any) => order?.patientDOB,
      formatter: (value: string) => formatDOB(value),
    },
    {
      key: "ssn",
      label: "SSN",
      accessor: (order: any) => order?.patientSSN || "---",
    },
    {
      key: "mrn",
      label: "MRN",
      accessor: (order: any) => order?.patientMRN || "---",
    },
    {
      key: "icd10",
      label: "ICD-10",
      accessor: (order: any) => order?.icdCodeDetails?.value || "---",
    },
    {
      key: "organization",
      label: "Organization",
      accessor: (order: any) => order?.organization?.name || "---",
    },
  ];

  // Column configurations for Patients table
  const patientsColumns = [
    {
      key: "patientName",
      label: "Patient Name",
      accessor: (patient: any) =>
        `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim() ||
        "---",
      highlight: true,
    },
    {
      key: "dob",
      label: "DOB",
      accessor: (patient: any) => patient?.birthDate,
      formatter: (value: string) => formatDOB(value),
    },
    {
      key: "ssn",
      label: "SSN",
      accessor: (patient: any) => patient?.ssn || "---",
    },
    {
      key: "mrn",
      label: "MRN",
      accessor: (patient: any) => patient?.mrn || "---",
    },
    {
      key: "organization",
      label: "Organization",
      accessor: (patient: any) => getOrganizationName(patient?.organizationId),
    },
  ];

  return (
    <div
      ref={dropDownRef}
      className={`absolute bg-white shadow-lg top-[36px] z-[10000] border border-dropDownBorder rounded-[12px] overflow-hidden left-0 right-0 lg:left-0 lg:w-[800px] lg:right-auto ${
        showDateInput ? "max-h-[400px]" : "max-h-[600px]"
      }`}
    >
      <div className="overflow-y-auto max-h-[550px]">
        {shouldShowNavigationSection && (
          <NavigationSection
            title="Navigation"
            results={navigationResults}
            isLoading={false}
            displayQuery={displayQuery}
            searchQuery={searchQuery}
            onNavigate={handleNavigationClick}
            highlightMatchingText={highlightMatchingText}
          />
        )}
        {/* Test Orders Section */}
        {searchText && (
          <SearchSection
            title="Test Order"
            count={ordersTotal}
            isLoading={isLoadingOrders}
            data={ordersData}
            columns={testOrdersColumns}
            displayQuery={displayQuery}
            searchQuery={searchQuery}
            onViewMore={handleViewMoreTestOrders}
            onRowClick={handleOrderClick}
            highlightMatchingText={highlightMatchingText}
            showBorder
          />
        )}

        {/* Patients Section */}
        <SearchSection
          title="Patients"
          count={patientsTotal}
          isLoading={isLoadingPatients}
          data={patientsData}
          columns={patientsColumns}
          displayQuery={displayQuery}
          searchQuery={searchQuery}
          onViewMore={handleViewMorePatients}
          onRowClick={(patient: any) => handlePatientClick(patient.id)}
          highlightMatchingText={highlightMatchingText}
          footer={
            <AddPatientButton
              searchText={searchText || ""}
              onClick={handleAddPatient}
            />
          }
        />
      </div>
    </div>
  );
}

function PatientSearchInput({
  toggleList,
  searchText,
  setShowList,
  setSearchText,
  showDateInput,
  setShowDateInput,
  onDateSelect,
  className = "",
  mode,
  onFocus,
}: Readonly<SearchInputProps>): JSX.Element {
  const { isTourActive } = useAdminContext();

  function handleInput(e: ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    // Don't open dropdown immediately - it will open when searchText has value
    setSearchText(value);
    setShowDateInput(false);
  }

  function handleFocus() {
    if (onFocus) {
      onFocus();
    }
  }

  return (
    <input
      placeholder={
        mode === "datalist"
          ? "Search using Patient's Name, SSN or MRN."
          : dataConstants.globalSearchPlaceholder
      }
      className={`patientSearch px-6 truncate w-full ${className}`}
      onChange={handleInput}
      onFocus={handleFocus}
      value={searchText === null ? "" : searchText}
      disabled={isTourActive}
    />
  );
}

interface PatientSearchDataListProps extends SearchInputProps {
  data: PatientDropDownItemProps[];
  searching: boolean;
  oktaAuth: any;
  mode: SearchMode;
  closeInput: () => void;
}

function PatientSearchDataList({
  searchText,
  setSearchText,
  setShowList,
  toggleList,
  data,
  searching,
  oktaAuth,
  setShowDateInput,
  showDateInput,
  mode,
  closeInput,
}: Readonly<PatientSearchDataListProps>): JSX.Element {
  const { visible, setVisible, setPopUp, setPopUpTitle } =
    useContext(PopUpContext);

  /**
   * Parses the search text and returns an object with firstName, middleInitial, and lastName
   * Logic:
   * - 3+ spaces (4+ parts): firstName, middleInitial, lastName (first 3 parts)
   * - 2 spaces (3 parts): firstName, middleInitial, lastName
   * - 1 space (2 parts): firstName, lastName
   * - 0 spaces (1 part): firstName only
   */
  function parseSearchTextToPatientInfo(searchText: string): OnlineOrderPopupValues {
    const trimmedText = (searchText || "").trim();
    
    // Start with default values
    const defaultValues: OnlineOrderPopupValues = {
      firstName: "",
      middleInitial: "",
      lastName: "",
      birthDate: "",
      gender: "",
      email: "",
      address: "",
      city: "",
      state: "",
      phone: "",
      zipcode: "",
      securityInfo: {
        ssn: "",
        mrn: "",
      },
    };

    if (!trimmedText) {
      return defaultValues;
    }

    const parts = trimmedText.split(/\s+/).filter(part => part.length > 0);
    
    if (parts.length >= 3) {
      // 3+ parts: firstName, middleInitial (first letter), lastName
      return {
        ...defaultValues,
        firstName: parts[0],
        middleInitial: parts[1].charAt(0).toUpperCase(),
        lastName: parts.slice(2).join(" "), // Join remaining parts as lastName
      };
    } else if (parts.length === 2) {
      // 2 parts: firstName, lastName
      return {
        ...defaultValues,
        firstName: parts[0],
        middleInitial: "",
        lastName: parts[1],
      };
    } else {
      // 1 part: firstName only
      return {
        ...defaultValues,
        firstName: parts[0],
        middleInitial: "",
        lastName: "",
      };
    }
  }

  function createPatient(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    
    // Parse the search text to extract patient name information
    const parsedPatientInfo = parseSearchTextToPatientInfo(searchText || "");
    
    setVisible(!visible);
    setPopUpTitle("Create a Patient");
    setPopUp(
      <CreatePatientPopup 
        oktaAuth={oktaAuth} 
        patientInformation={parsedPatientInfo}
      />
    );
  }

  return (
    <div className="bg-sideNavGray rounded-md border shadow-md border-dropDownBorder overflow-x-auto w-auto max-w-[80vw] lg:min-w-[800px]">
      <div className="p-6">
        <CustomText
          text="Patient"
          fontWeight="font-semibold"
          className="mb-2"
        />
        <div className={`patientSearchContainer !w-full m-0  max-h-[36px]`}>
          <PatientSearchInput
            searchText={searchText}
            mode={mode}
            setSearchText={setSearchText}
            setShowList={setShowList}
            toggleList={toggleList}
            setShowDateInput={setShowDateInput}
            showDateInput={showDateInput}
          />
        </div>
      </div>
      {searchText && (
        <div className="border-t border-dropDownBorder">
          <div className="px-6 max-h-[300px] overflow-auto">
            <CustomText
              text="Existing Patients"
              fontWeight="font-semibold"
              className="pt-4 pb-2 bg-sideNavGray sticky top-0"
            />
            <PatientsList
              setShowList={setShowList}
              data={data}
              searchText={searchText}
              mode={"datalist"}
              closeInput={closeInput}
              searching={searching}
            />
          </div>
          <button
            className="py-2 px-8 shadow-[0_0px_1px_1px_rgba(0,0,0,0.1)] flex md:flex-row flex-col w-full items-center rounded-b-md border-t mt-4 max-w-[800px]"
            onClick={createPatient}
          >
            <CustomText
              text="+ ADD"
              fontSize="md:text-sm text-xs"
              fontWeight="font-semibold"
            />
            <CustomText
              text={searchText}
              fontSize="md:text-sm text-xs"
              fontWeight="font-semibold"
              className="text-primary capitalize mx-2 truncate"
            />
            <CustomText
              text={" as a new patient"}
              fontSize="md:text-sm text-xs"
              fontWeight="font-semibold"
              className="uppercase"
            />
          </button>
        </div>
      )}
    </div>
  );
}

function PatientSearch({
  mode,
  containerClassName,
  oktaAuth,
  closeInput,
}: PatientSearchProps): JSX.Element {
  const isDatalistMode = mode === "datalist";
  const location = useLocation();
  const [searchText, setSearchText] = useState<string>("");
  const [patientDob, setPatientDob] = useState("");
  const [inputText, setInputText] = useState<string>("");
  const [showList, setShowList] = useState<boolean>(false);
  const [showDateInput, setShowDateInput] = useState(false);
  const dropDownRef = useRef<any>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const { isTourActive } = useAdminContext();
  const toggleList = () => setShowList((prevState) => !prevState);
  const { data, isLoading } = useSearchPatients(
    searchText,
    patientDob,
    oktaAuth,
    { enabled: isDatalistMode }
  );

  // Handle calendar closing on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      // Check if click is outside calendar
      const isOutsideCalendar =
        !calendarRef.current || !calendarRef.current.contains(target);
      // Check if click is outside input container
      const isOutsideInput =
        !inputContainerRef.current ||
        !inputContainerRef.current.contains(target);

      // Close calendar if click is outside both calendar and input container
      if (isOutsideCalendar && isOutsideInput) {
        setShowDateInput(false);
      }
    }

    if (showDateInput) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDateInput]);

  // Handle dropdown closing on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      // Check if click is outside dropdown (only exists in non-datalist mode)
      const isOutsideDropdown =
        !dropDownRef?.current || !dropDownRef.current.contains(target);
      // Check if click is outside input container
      const isOutsideInput =
        !inputContainerRef?.current ||
        !inputContainerRef.current.contains(target);
      // Check if click is outside calendar
      const isOutsideCalendar =
        !calendarRef?.current || !calendarRef.current.contains(target);

      // Close dropdown/list if click is outside all relevant containers
      if (isOutsideDropdown && isOutsideInput && isOutsideCalendar) {
        // In datalist mode, don't clear search text - just close the list
        // This allows the "Add" button to remain visible
        if (!isDatalistMode) {
          setSearchText("");
          // Only clear inputText if there's no date selected
          if (!patientDob) {
            setInputText("");
          }
        }
        setShowList(false);
      }
    }

    if (showList) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showList, patientDob, isDatalistMode]);

  // Use custom hook for debounced date detection
  const { debouncedDateDetection, debounceSearch } = useDebouncedDateDetection({
    patientDob,
    setPatientDob,
    setInputText,
    setSearchText,
  });

  // Clear filter on screen/route change
  useEffect(() => {
    resetSearch();
    setShowList(false);
    setShowDateInput(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Update showList based on searchText or patientDob
  useEffect(() => {
    if (searchText || patientDob) {
      setShowList(true);
    } else {
      setShowList(false);
    }
  }, [searchText, patientDob]);

  function handleSearch(search: string) {
    // Update input text immediately for display
    setInputText(search);

    // Debounce the date detection - only check after user stops typing
    debouncedDateDetection(search);
  }

  function clearInputText() {
    setInputText("");
  }

  function resetSearch() {
    setSearchText("");
    setInputText("");
    setPatientDob("");
  }

  function handleCalendarClick(e: React.MouseEvent) {
    e.stopPropagation();
    const newShowDateInput = !showDateInput;
    setShowDateInput(newShowDateInput);
    // Close dropdown when calendar is opened
    if (newShowDateInput) {
      setShowList(false);
    }
  }

  if (isDatalistMode) {
    return (
      <PatientSearchDataList
        setShowDateInput={setShowDateInput}
        showDateInput={showDateInput}
        searchText={inputText}
        oktaAuth={oktaAuth}
        mode={mode}
        setSearchText={handleSearch}
        setShowList={setShowList}
        toggleList={toggleList}
        closeInput={closeInput}
        data={
          data?.data ? data?.data?.map((patient) => ({ ...patient, mode })) : []
        }
        searching={isLoading}
      />
    );
  }

  return (
    <div className="relative mx-2 md:mx-6" id="step2">
      <div
        ref={inputContainerRef}
        className={`patientSearchContainer max-h-[36px] ${containerClassName}`}
      >
        <div className="absolute mt-2 z-10 pointer-events-none inset-y-0 flex items-center">
          <SearchIcon stroke="#667085" className="w-6 h-6" />
        </div>
        <PatientSearchInput
          searchText={inputText}
          setSearchText={handleSearch}
          setShowDateInput={setShowDateInput}
          showDateInput={showDateInput}
          setShowList={setShowList}
          toggleList={toggleList}
          onDateSelect={(date) => {
            const formattedDate = moment(date).format("MM-DD-YYYY");
            setSearchText("");
            setPatientDob(formattedDate);
            setInputText(formattedDate);
            setShowDateInput(false);
            setShowList(true);
          }}
          onFocus={() => {
            // Open dropdown when input is focused and there's a filter or search text
            if (searchText || patientDob) {
              setShowList(true);
            }
          }}
        />
        <button
          type="button"
          onClick={handleCalendarClick}
          className="absolute right-3 z-10 cursor-pointer inset-y-0 flex items-center"
          disabled={isTourActive}
        >
          <CalendarIcon
            className="w-4 h-4"
            stroke={showDateInput ? "#18B5B7" : "#667085"}
          />
        </button>
      </div>
      {showDateInput && (
        <div
          ref={calendarRef}
          className="absolute top-full right-0 mt-2 bg-white border border-dropDownBorder rounded-lg shadow-lg z-[10001]"
        >
          <CustomCalendar
            value={patientDob || undefined}
            onChange={(date) => {
              const formattedDate = moment(date).format("MM-DD-YYYY");
              setSearchText("");
              setPatientDob(formattedDate);
              setInputText(formattedDate);
              setShowDateInput(false);
              setShowList(true);
            }}
          />
        </div>
      )}
      {showList && (
        <GlobalSearchDropdown
          dropDownRef={dropDownRef}
          setShowList={setShowList}
          setShowDateInput={setShowDateInput}
          showDateInput={showDateInput}
          searchText={inputText}
          clearInputText={clearInputText}
          setPatientDOB={(date) => {
            const formattedDate = moment(date).format("MM-DD-YYYY");
            setSearchText("");
            setPatientDob(formattedDate);
            setInputText(formattedDate);
            setShowDateInput(false);
          }}
          oktaAuth={oktaAuth}
          closeInput={closeInput}
          searchQuery={searchText}
          patientDob={patientDob}
          resetSearch={resetSearch}
        />
      )}
    </div>
  );
}

export default PatientSearch;
